import React, { useState, useMemo, useCallback } from "react";
import useApiEp from "../../hooks/useApiEp";
import MuPb from "../../widgets/MuPb";
import BackBtn from "../../widgets/BackBtn";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Container,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Tooltip,
  LinearProgress,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { getErrorResponse, getHeader, toastError } from "../../utils/util";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { toast } from "react-toastify";

const fmt = (n) => (n ?? 0).toLocaleString();
const pct = (a, b) => (b === 0 ? "0%" : `${Math.round((a / b) * 100)}%`);

const SORT_FIELDS = [
  { key: "totalMessages", label: "Messages" },
  { key: "totalSeen", label: "Seen" },
  { key: "totalQualifiedLeads", label: "Leads" },
];

const SummaryCard = ({ label, value }) => (
  <Box
    sx={{
      flex: "1 1 120px",
      bgcolor: "action.hover",
      borderRadius: 2,
      p: 1.5,
    }}
  >
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="h6" fontWeight={500}>
      {value}
    </Typography>
  </Box>
);

const WabaLeadsStats = () => {
  const { data, isLoading, isError, error, refetch } = useApiEp(
    `/root/get-waba-leads-stats`
  );

  const [search, setSearch] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [sortKey, setSortKey] = useState("totalQualifiedLeads");
  const [sortDir, setSortDir] = useState("desc");

  const handleRefresh = useCallback(async () => {
    try {
      setRefreshing(true);
      const response = await axios.get(
        `${API_ENDPOINT}/root/refresh-waba-leads-stats`,
        getHeader()
      );
      refetch();
      toast.success(response.data.message);
    } catch (error) {
      toastError(error);
    } finally {
      setRefreshing(false);
    }
  }, []);

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    const filtered = q
      ? data.filter(
          (r) =>
            r.customerId?.toLowerCase().includes(q) ||
            r._id?.toLowerCase().includes(q)
        )
      : [...data];

    filtered.sort((a, b) => {
      const av = a[sortKey] ?? 0;
      const bv = b[sortKey] ?? 0;
      const cmp = typeof av === "string" ? av.localeCompare(bv) : av - bv;
      return sortDir === "desc" ? -cmp : cmp;
    });
    return filtered;
  }, [data, search, sortKey, sortDir]);

  const summary = useMemo(() => {
    if (!data) return {};
    const totalMessages = data.reduce((s, r) => s + (r.totalMessages ?? 0), 0);
    const totalSeen = data.reduce((s, r) => s + (r.totalSeen ?? 0), 0);
    const totalLeads = data.reduce(
      (s, r) => s + (r.totalQualifiedLeads ?? 0),
      0
    );
    return {
      customers: data.length,
      totalMessages,
      totalSeen,
      totalLeads,
      seenRate: pct(totalSeen, totalMessages),
    };
  }, [data]);

  const maxMsg = useMemo(
    () => Math.max(...(data ?? []).map((r) => r.totalMessages ?? 0), 1),
    [data]
  );

  if (isLoading) return <MuPb />;
  if (isError) return <Alert severity="error">{getErrorResponse(error)}</Alert>;

  return (
    <Container sx={{ mt: 3 }}>
      <BackBtn />

      {/* Summary row */}
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", my: 2.5 }}>
        <SummaryCard label="Customers" value={summary.customers} />
        <SummaryCard
          label="Total messages"
          value={fmt(summary.totalMessages)}
        />
        <SummaryCard label="Total seen" value={fmt(summary.totalSeen)} />
        <SummaryCard label="Avg seen rate" value={summary.seenRate} />
        <SummaryCard label="Total leads" value={fmt(summary.totalLeads)} />
      </Box>

      {/* Controls */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          flexWrap: "wrap",
          alignItems: "center",
          mb: 2,
        }}
      >
        <TextField
          size="small"
          placeholder="Search by customer ID or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: "1 1 220px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Tooltip title="Refresh">
          {refreshing ? (
            <CircularProgress />
          ) : (
            <IconButton size="small" onClick={handleRefresh}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          )}
        </Tooltip>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={sortKey}
          onChange={(_, v) => v && handleSort(v)}
        >
          {SORT_FIELDS.map((f) => (
            <ToggleButton key={f.key} value={f.key}>
              {f.label}{" "}
              {sortKey === f.key ? (sortDir === "desc" ? "↓" : "↑") : ""}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        mb={1}
      >
        Showing {rows.length} of {data?.length ?? 0} customers
      </Typography>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortKey === "customerId"}
                  direction={sortKey === "customerId" ? sortDir : "desc"}
                  onClick={() => handleSort("customerId")}
                >
                  Customer
                </TableSortLabel>
              </TableCell>
              {SORT_FIELDS.map((f) => (
                <TableCell key={f.key} align="right">
                  <TableSortLabel
                    active={sortKey === f.key}
                    direction={sortKey === f.key ? sortDir : "desc"}
                    onClick={() => handleSort(f.key)}
                  >
                    {f.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="right">Seen rate</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const seenRate =
                row.totalMessages > 0
                  ? Math.round((row.totalSeen / row.totalMessages) * 100)
                  : 0;
              const barWidth = Math.round(
                ((row.totalMessages ?? 0) / maxMsg) * 100
              );
              return (
                <TableRow key={row._id} hover>
                  <TableCell sx={{ maxWidth: 220 }}>
                    <Tooltip title={row.customerId} placement="top">
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        noWrap
                        sx={{ maxWidth: 200 }}
                      >
                        {row.customerId}
                      </Typography>
                    </Tooltip>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontFamily="monospace"
                    >
                      {row._id}
                    </Typography>
                  </TableCell>

                  <TableCell align="right">
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      <Typography variant="body2">
                        {fmt(row.totalMessages)}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={barWidth}
                        sx={{
                          borderRadius: 2,
                          height: 4,
                          width: 80,
                          ml: "auto",
                        }}
                      />
                    </Box>
                  </TableCell>

                  <TableCell align="right">{fmt(row.totalSeen)}</TableCell>

                  <TableCell align="right">
                    {row.totalQualifiedLeads > 0 ? (
                      <Chip
                        label={fmt(row.totalQualifiedLeads)}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        0
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      color={
                        seenRate >= 60
                          ? "success.main"
                          : seenRate >= 30
                          ? "warning.main"
                          : "text.secondary"
                      }
                    >
                      {seenRate}%
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default WabaLeadsStats;
